import {useCallback, useState} from 'react';

import type {User} from 'nutshell-core/users/users-types';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {WidgetFragment} from 'nutshell-graphql-types';

import {CreateInfo} from '../../../shared/summary-widget/create-info/create-info';
import {PeepLastContacted} from '../../../shared/summary-widget/peep-last-contacted';
import {PeepTerritory} from '../../../shared/summary-widget/peep-territory';
import {PeepTagsEditor} from '../../../shared/tags-editor/peep-tags-editor';

import {SummaryWidgetContent} from '../../../../core-entity/widgets/summary-widget/summary-widget-content';

import {useGetContactLastContactedTime} from '../../graphql/hooks/use-get-contact-last-contacted-time';

import {useGetContactSummary} from './graphql/hooks/use-get-contact-summary';
import {useUpdateContactSummary} from './graphql/hooks/use-update-contact-summary';

interface Props {
    contactId: string;
    widget: WidgetFragment;
    user: User;
}
export function ContactSummaryWidget(props: Props) {
    const [error, setError] = useState<string | null | undefined>(null);

    const {contactSummary, isLoading} = useGetContactSummary(props.contactId);
    const {contactLastContact, isLoading: isLoadingContactLastContact} =
        useGetContactLastContactedTime(props.contactId);
    const {updateContact} = useUpdateContactSummary();

    const handleUpdateTags = useCallback(
        (tags: Tag[]) => {
            if (!contactSummary) return Promise.resolve();

            return updateContact(
                {
                    id: contactSummary.id,
                    tags: tags.map((tag) => ({tagType: 'CONTACT' as const, name: tag.name})),
                },
                contactSummary,
                undefined,
                tags
            );
        },
        [updateContact, contactSummary]
    );

    const handleUpdateContactTerritory = useCallback(
        (territoryId: string | null, territoryName: string | null) => {
            if (!contactSummary) {
                return Promise.resolve();
            }

            return updateContact(
                {
                    id: contactSummary.id,
                    territoryId,
                },
                contactSummary,
                territoryName
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateContact, contactSummary]
    );

    return (
        <SummaryWidgetContent
            widget={props.widget}
            fields={
                <>
                    <PeepTerritory
                        territoryId={contactSummary?.territory?.id ?? null}
                        onUpdatePeepTerritory={handleUpdateContactTerritory}
                        canAccessSetup={props.user.permissions.canAccessSetup}
                    />
                    <PeepLastContacted
                        entityType='contact'
                        lastContactedTime={contactLastContact?.lastContactedTime ?? null}
                        myLastContactedTime={contactLastContact?.myLastContactedTime ?? null}
                        isLoadingPeepLastContact={isLoading}
                    />
                    <CreateInfo
                        creatorUser={contactSummary?.creatorUser ?? null}
                        createdTime={contactSummary?.createdTime ?? null}
                    />
                </>
            }
            isLoading={isLoading || isLoadingContactLastContact}
            tags={
                contactSummary ? (
                    <PeepTagsEditor
                        entityId={props.contactId}
                        entityType='CONTACT'
                        user={props.user}
                        tagMaps={contactSummary.tagMaps?.edges}
                        onUpdateTags={handleUpdateTags}
                        maxItems={5}
                    />
                ) : null
            }
            error={error}
            setError={setError}
        />
    );
}
